/**
 * @packageDocumentation
 * Renders the initial sound preference popup and blocks interaction until a choice is made.
 * Layout is Figma-driven for BOTH landscape (1920x1080) and portrait (1080x1920), using the
 * same designRoot + LTWH placement pattern used for the preloader/instruction screens.
 */

import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { overlay } from "../app";
import { SoundManager } from "./sounds";

export type SoundPromptOptions = {
  width: number;
  height: number;
  onChoice: (enabled: boolean) => void;
};

export class SoundPrompt extends PIXI.Container {
  private readonly choiceHandler: (enabled: boolean) => void;
  private resolved = false;

  // Fullscreen dimmer (blocks clicks behind the modal)
  private readonly dimmer: PIXI.Graphics;

  // Root container representing the Figma frame
  private readonly designRoot = new PIXI.Container();

  // Modal and contents
  private readonly modal = new PIXI.Container();
  private bg!: PIXI.Sprite;
  private title!: PIXI.Text;
  private subtitle!: PIXI.Text;
  private orText?: PIXI.Text;
  private btnOn!: PIXI.Sprite;
  private btnOff!: PIXI.Sprite;

  // Resize bookkeeping
  private raf = 0;
  private onResizeBound = () => this.requestLayout();

  // ===== FIGMA SOURCE OF TRUTH =====

  // Landscape (assumed 1920x1080)
  private readonly FIGMA_L = {
    BASE_W: 1920,
    BASE_H: 1080,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    MODAL: { left: 161, top: 217, w: 1596, h: 648 },
    TITLE: { left: 689, top: 295, w: 526, h: 107 },
    SUBTITLE: { left: 492, top: 450, w: 921, h: 53 },
    OR_TEXT: { left: 919, top: 633, w: 98, h: 62 },
    BTN_ON: { left: 643, top: 592, w: 193, h: 193 },
    BTN_OFF: { left: 1080, top: 592, w: 193, h: 193 },
  } as const;

  // Portrait (1080x1920)
  private readonly FIGMA_P = {
    BASE_W: 1080,
    BASE_H: 1920,
    ROOT_OFFSET_X: 0,
    ROOT_OFFSET_Y: 0,

    // left: calc(50% - 1455.16px/2 - 0.42px)
    // => 540 - 727.58 - 0.42 = -188.00
    MODAL: { left: -188.0, top: 394.59, w: 1455.16, h: 590.82 },
    TITLE: { left: 293, top: 466, w: 533, h: 144 },
    SUBTITLE: { left: 113.79, top: 607.03, w: 839.72, h: 48.32 },
    OR_TEXT: { left: 489.67, top: 793.49, w: 98, h: 62 },
    BTN_ON: { left: 251.47, top: 736.5, w: 175.97, h: 175.97 },
    BTN_OFF: { left: 649.9, top: 736.5, w: 175.97, h: 175.97 },
  } as const;

  constructor(options: SoundPromptOptions) {
    super();
    this.choiceHandler = options.onChoice;
    this.sortableChildren = true;

    // Show HTML overlay (existing behavior in your project)
    overlay.style.display = "block";

    // Dimmer blocks interaction behind modal
    this.dimmer = this.createDimmer(options.width, options.height);
    this.addChild(this.dimmer);

    // All modal content lives in designRoot
    this.designRoot.sortableChildren = true;
    this.addChild(this.designRoot);

    this.modal.sortableChildren = true;
    this.designRoot.addChild(this.modal);

    this.buildUI();

    // Initial layout + wire resize listeners
    this.applyResponsiveLayout();
    this.addLayoutListeners();
  }

  // ---------------------------------------------------------------------------
  // UI construction
  // ---------------------------------------------------------------------------

  private buildUI() {
    // Background: landscape uses modal_large; portrait uses modal_gameSound.
    // Provide a safe fallback to "popup" if needed.
    this.bg = new PIXI.Sprite(this.safeTexture(["modal_large", "modal_gameSound", "popup"]));
    this.bg.anchor.set(0.5);
    this.bg.zIndex = 1;
    this.modal.addChild(this.bg);

    this.title = new PIXI.Text({
      text: "GAME SOUND",
      style: new PIXI.TextStyle({
        fontFamily: "RoadRage",
        fontSize: 150,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
        lineHeight: 165,
      }),
    });
    this.title.anchor.set(0.5);
    this.title.zIndex = 2;
    this.modal.addChild(this.title);

    this.subtitle = new PIXI.Text({
      text: "Do you want music and sound effects...",
      style: new PIXI.TextStyle({
        fontFamily: "PermanentMarker",
        fontSize: 40,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
        lineHeight: 58,
        wordWrap: true,
        wordWrapWidth: 1000, // will be overridden by LTWH fit
      }),
    });
    this.subtitle.anchor.set(0.5);
    this.subtitle.zIndex = 2;
    this.modal.addChild(this.subtitle);

    // Optional "or" art for landscape.
    // If you don't have a dedicated texture loaded, this will simply not render.
    // ✅ "or" text between buttons (Figma-driven)
    this.orText = new PIXI.Text({
      text: "or",
      style: new PIXI.TextStyle({
        fontFamily: "PermanentMarker",
        fontSize: 56,
        fontWeight: "400",
        fill: 0xffffff,
        align: "center",
      }),
    });
    this.orText.anchor.set(0.5);
    this.orText.zIndex = 2;
    this.modal.addChild(this.orText);


    // Buttons are image sprites (btn_on / btn_off) as requested
    this.btnOn = new PIXI.Sprite(this.safeTexture(["btn_on"]));
    this.btnOff = new PIXI.Sprite(this.safeTexture(["btn_off"]));

    for (const b of [this.btnOn, this.btnOff]) {
      b.anchor.set(0.5);
      b.eventMode = "static";
      b.cursor = "pointer";
      b.zIndex = 3;
      this.addButtonInteractions(b);
      this.modal.addChild(b);
    }

    this.btnOn.on("pointertap", () => this.resolve(true));
    this.btnOff.on("pointertap", () => this.resolve(false));

    // Ensure modal itself doesn't eat clicks intended for buttons
    this.modal.eventMode = "passive";
  }

  private safeTexture(keys: string[]): PIXI.Texture {
    for (const k of keys) {
      if (Assets.cache.has(k)) return Assets.get(k) as PIXI.Texture;
    }
    // If none found, throw with a useful message.
    throw new Error(`SoundPrompt: none of these textures are loaded: ${keys.join(", ")}`);
  }

  private safeTextureOrNull(keys: string[]): PIXI.Texture | null {
    for (const k of keys) {
      if (Assets.cache.has(k)) return Assets.get(k) as PIXI.Texture;
    }
    return null;
  }

  private addButtonInteractions(btn: PIXI.Sprite) {
    const baseScale = { x: 1, y: 1 };
    const baseAlpha = 1;

    btn.on("pointerover", () => {
      btn.scale.set(baseScale.x * 1.05, baseScale.y * 1.05);
      btn.alpha = 0.9;
    });

    btn.on("pointerout", () => {
      btn.scale.set(baseScale.x, baseScale.y);
      btn.alpha = baseAlpha;
    });

    // Keep baseScale in sync after layout resizes
    (btn as any).__setBaseScale = (sx: number, sy: number) => {
      baseScale.x = sx;
      baseScale.y = sy;
      btn.scale.set(sx, sy);
      btn.alpha = baseAlpha;
    };
  }

  // ---------------------------------------------------------------------------
  // Overlay / Dimmer
  // ---------------------------------------------------------------------------

  private createDimmer(width: number, height: number): PIXI.Graphics {
    const g = new PIXI.Graphics()
      .beginFill(0x000000, 0.65)
      .drawRect(0, 0, Math.max(width, 1), Math.max(height, 1))
      .endFill();
    g.eventMode = "static";
    g.cursor = "default";
    g.zIndex = 0;
    return g;
  }

  private updateDimmerToScreen(screenW: number, screenH: number) {
    this.dimmer.clear()
      .beginFill(0x000000, 0.65)
      .drawRect(0, 0, Math.max(screenW, 1), Math.max(screenH, 1))
      .endFill();

    this.dimmer.hitArea = new PIXI.Rectangle(0, 0, Math.max(screenW, 1), Math.max(screenH, 1));
  }

  // ---------------------------------------------------------------------------
  // Layout helpers (same pattern as preloader/instruction)
  // ---------------------------------------------------------------------------

  private getScreenSize() {
    // Prefer renderer screen size if available
    const app = (globalThis as any).app ?? (globalThis as any).__PIXI_APP__ ?? null;
    const screenW = app?.renderer?.screen?.width ?? (this.parent as any)?.width ?? (window.visualViewport?.width ?? window.innerWidth ?? 1920);
    const screenH = app?.renderer?.screen?.height ?? (this.parent as any)?.height ?? (window.visualViewport?.height ?? window.innerHeight ?? 1080);
    return { screenW, screenH };
  }

  private layoutDesignRoot(
    screenW: number,
    screenH: number,
    cfg: { BASE_W: number; BASE_H: number; ROOT_OFFSET_X: number; ROOT_OFFSET_Y: number }
  ) {
    const s = Math.min(screenW / cfg.BASE_W, screenH / cfg.BASE_H);
    this.designRoot.scale.set(s);
    this.designRoot.x = (screenW - cfg.BASE_W * s) / 2 + cfg.ROOT_OFFSET_X;
    this.designRoot.y = (screenH - cfg.BASE_H * s) / 2 + cfg.ROOT_OFFSET_Y;
  }

  private figmaCenter(left: number, top: number, w: number, h: number) {
    return { x: left + w / 2, y: top + h / 2 };
  }

  private placeFromFigmaLTWH(
    obj: PIXI.Container,
    left: number,
    top: number,
    w: number,
    h: number,
    opts?: { fitToSize?: boolean; nudgeX?: number; nudgeY?: number }
  ) {
    const { fitToSize = false, nudgeX = 0, nudgeY = 0 } = opts ?? {};

    if (fitToSize && (obj as any).width != null && (obj as any).height != null) {
      const anyObj = obj as any as { width: number; height: number; scale: PIXI.ObservablePoint };
      const sx = w / Math.max(anyObj.width, 0.0001);
      const sy = h / Math.max(anyObj.height, 0.0001);
      anyObj.scale.set(anyObj.scale.x * sx, anyObj.scale.y * sy);

      // Keep hover interactions consistent after rescale
      const setBase = (obj as any).__setBaseScale as undefined | ((sx: number, sy: number) => void);
      if (setBase) setBase(anyObj.scale.x, anyObj.scale.y);
    }

    const b = obj.getLocalBounds();
    obj.pivot.set(b.x + b.width / 2, b.y + b.height / 2);

    const c = this.figmaCenter(left, top, w, h);
    obj.position.set(c.x + nudgeX, c.y + nudgeY);
  }

  private requestLayout() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.applyResponsiveLayout());
  }

  private addLayoutListeners() {
    window.addEventListener("resize", this.onResizeBound);
    window.addEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.addEventListener("resize", this.onResizeBound);
    window.visualViewport?.addEventListener("scroll", this.onResizeBound);
  }

  private removeLayoutListeners() {
    window.removeEventListener("resize", this.onResizeBound);
    window.removeEventListener("orientationchange", this.onResizeBound);
    window.visualViewport?.removeEventListener("resize", this.onResizeBound);
    window.visualViewport?.removeEventListener("scroll", this.onResizeBound);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private applyResponsiveLayout() {
    const { screenW, screenH } = this.getScreenSize();

    // Keep dimmer synced to the real screen size
    this.updateDimmerToScreen(screenW, screenH);

    const portrait = screenH > screenW;
    if (portrait) this.applyPortraitLayout(screenW, screenH);
    else this.applyLandscapeLayout(screenW, screenH);

    // Ensure zIndex sorting is respected
    this.sortChildren();
    this.designRoot.sortChildren();
    this.modal.sortChildren();
  }

  private applyLandscapeLayout(screenW: number, screenH: number) {
    this.layoutDesignRoot(screenW, screenH, this.FIGMA_L);

    // Background (modal)
    this.bg.scale.set(1);
    this.placeFromFigmaLTWH(this.bg as any, this.FIGMA_L.MODAL.left, this.FIGMA_L.MODAL.top, this.FIGMA_L.MODAL.w, this.FIGMA_L.MODAL.h, {
      fitToSize: true,
    });

    // Title and subtitle
    this.title.scale.set(1);
    this.placeFromFigmaLTWH(this.title as any, this.FIGMA_L.TITLE.left, this.FIGMA_L.TITLE.top, this.FIGMA_L.TITLE.w, this.FIGMA_L.TITLE.h, {
      fitToSize: true,
    });

    // Subtitle is centered text; keep it within the target width.
    this.subtitle.style.wordWrapWidth = this.FIGMA_L.SUBTITLE.w;
    this.subtitle.scale.set(1);
    this.placeFromFigmaLTWH(
      this.subtitle as any,
      this.FIGMA_L.SUBTITLE.left,
      this.FIGMA_L.SUBTITLE.top,
      this.FIGMA_L.SUBTITLE.w,
      this.FIGMA_L.SUBTITLE.h,
      { fitToSize: true }
    );

    // ✅ OR text
    if (this.orText) {
      this.orText.scale.set(1);
      this.placeFromFigmaLTWH(
        this.orText as any,
        this.FIGMA_L.OR_TEXT.left,
        this.FIGMA_L.OR_TEXT.top,
        this.FIGMA_L.OR_TEXT.w,
        this.FIGMA_L.OR_TEXT.h,
        { fitToSize: true }
      );
      this.orText.visible = true;
    }


    // Buttons
    this.btnOn.scale.set(1);
    this.placeFromFigmaLTWH(this.btnOn as any, this.FIGMA_L.BTN_ON.left, this.FIGMA_L.BTN_ON.top, this.FIGMA_L.BTN_ON.w, this.FIGMA_L.BTN_ON.h, {
      fitToSize: true,
    });

    this.btnOff.scale.set(1);
    this.placeFromFigmaLTWH(this.btnOff as any, this.FIGMA_L.BTN_OFF.left, this.FIGMA_L.BTN_OFF.top, this.FIGMA_L.BTN_OFF.w, this.FIGMA_L.BTN_OFF.h, {
      fitToSize: true,
    });

    // Modal container should stay at (0,0) in designRoot space
    this.modal.position.set(0, 0);
    this.modal.scale.set(1);
  }

  private applyPortraitLayout(screenW: number, screenH: number) {
    this.layoutDesignRoot(screenW, screenH, this.FIGMA_P);

    // Background (modal)
    // In portrait, if you have a dedicated texture, swap it.
    if (Assets.cache.has("modal_gameSound") && this.bg.texture !== (Assets.get("modal_gameSound") as PIXI.Texture)) {
      this.bg.texture = Assets.get("modal_gameSound") as PIXI.Texture;
    }

    this.bg.scale.set(1);
    this.placeFromFigmaLTWH(this.bg as any, this.FIGMA_P.MODAL.left, this.FIGMA_P.MODAL.top, this.FIGMA_P.MODAL.w, this.FIGMA_P.MODAL.h, {
      fitToSize: true,
    });

    this.title.scale.set(1);
    this.placeFromFigmaLTWH(this.title as any, this.FIGMA_P.TITLE.left, this.FIGMA_P.TITLE.top, this.FIGMA_P.TITLE.w, this.FIGMA_P.TITLE.h, {
      fitToSize: true,
    });

    this.subtitle.style.wordWrapWidth = this.FIGMA_P.SUBTITLE.w;
    this.subtitle.scale.set(1);
    this.placeFromFigmaLTWH(
      this.subtitle as any,
      this.FIGMA_P.SUBTITLE.left,
      this.FIGMA_P.SUBTITLE.top,
      this.FIGMA_P.SUBTITLE.w,
      this.FIGMA_P.SUBTITLE.h,
      { fitToSize: true }
    );

    // ✅ OR text (portrait)
    if (this.orText) {
      this.orText.scale.set(1);
      this.placeFromFigmaLTWH(
        this.orText as any,
        this.FIGMA_P.OR_TEXT.left,
        this.FIGMA_P.OR_TEXT.top,
        this.FIGMA_P.OR_TEXT.w,
        this.FIGMA_P.OR_TEXT.h,
        { fitToSize: true }
      );
      this.orText.visible = true;
    }


    this.btnOn.scale.set(1);
    this.placeFromFigmaLTWH(this.btnOn as any, this.FIGMA_P.BTN_ON.left, this.FIGMA_P.BTN_ON.top, this.FIGMA_P.BTN_ON.w, this.FIGMA_P.BTN_ON.h, {
      fitToSize: true,
    });

    this.btnOff.scale.set(1);
    this.placeFromFigmaLTWH(this.btnOff as any, this.FIGMA_P.BTN_OFF.left, this.FIGMA_P.BTN_OFF.top, this.FIGMA_P.BTN_OFF.w, this.FIGMA_P.BTN_OFF.h, {
      fitToSize: true,
    });

    this.modal.position.set(0, 0);
    this.modal.scale.set(1);
  }

  // ---------------------------------------------------------------------------
  // Resolution
  // ---------------------------------------------------------------------------

  private resolve(enabled: boolean) {
    if (this.resolved) return;

    if (enabled) SoundManager.Btnclick_1SndPlay();

    this.resolved = true;
    overlay.style.display = "none";

    // Disable interactivity
    this.btnOn.eventMode = "none";
    this.btnOff.eventMode = "none";

    this.choiceHandler(enabled);
  }

  override destroy(options?: boolean | PIXI.DestroyOptions) {
    this.removeLayoutListeners();
    // Ensure the HTML overlay is not left visible if the prompt is destroyed early
    overlay.style.display = "none";

    super.destroy(options as any);
  }
}
